// src/components/common/PlayersPerfContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { fetchAuth } from "./api";
import { parseSeasonStartYear, useSeason } from "./SeasonContext";

const PlayersPerfContext = createContext(null);
const PLAYERS_PERF_TIMEOUT_MS = 90000;
const PLAYERS_PERF_RETRY_DELAYS_MS = [400];

function devLog(method, ...args) {
  if (import.meta.env.DEV) {
    console[method](...args);
  }
}

const EMPTY_PERF_STATE = {
  players: [],
  allPlayers: [],
  playersById: {},
  mostRecentSimulation: null,
  clubInfo: null,
  loading: false,
  error: null,
  isFallbackData: false,
};

function emptyPerfState(overrides = {}) {
  return { ...EMPTY_PERF_STATE, ...overrides };
}

function normalizeMostRecentSimulation(value) {
  if (value == null) return null;
  const text = String(value).trim();
  if (!text || text.toUpperCase() === "N/A") return null;
  return text;
}

function scopePlayersToClub(rawPlayers, clubId) {
  if (!clubId) return rawPlayers;
  const expected = String(clubId).toLowerCase();
  const scoped = rawPlayers.filter((player) => {
    if (!player?.club_id) return true;
    return String(player.club_id).toLowerCase() === expected;
  });
  if (scoped.length === 0 && rawPlayers.length > 0) {
    devLog("warn", "[playersperfs] club filter emptied roster, keeping backend list", {
      expected,
      sampleClubId: rawPlayers[0]?.club_id,
      rawCount: rawPlayers.length,
    });
    return rawPlayers;
  }
  return scoped;
}

function buildPerfState(data) {
  const viewingClubId = data?.club_info?.id ? String(data.club_info.id) : null;
  const rawPlayers = scopePlayersToClub(
    Array.isArray(data?.players) ? data.players : [],
    viewingClubId
  );
  const players = rawPlayers.map((p) => normalizePlayer(p));
  const enabledPlayers = players.filter((p) => p.player_enabled === true);
  return {
    players: enabledPlayers,
    allPlayers: players,
    playersById: Object.fromEntries(enabledPlayers.map((p) => [p.id, p])),
    mostRecentSimulation: normalizeMostRecentSimulation(data?.most_recent_simulation),
    clubInfo: data?.club_info || null,
    loading: false,
    error: null,
    isFallbackData: false,
  };
}

function mergeAvatarsIntoState(perfState, avatars) {
  if (!avatars || typeof avatars !== "object") return perfState;
  const apply = (player) => {
    const pic = avatars[String(player.id)];
    return pic ? { ...player, profile_picture: pic } : player;
  };
  const allPlayers = (perfState.allPlayers || []).map(apply);
  const players = (perfState.players || []).map(apply);
  return {
    ...perfState,
    players,
    allPlayers,
    playersById: Object.fromEntries(players.map((p) => [p.id, p])),
  };
}

function isAbortLike(error) {
  const name = error?.name || "";
  const message = String(error?.message || "").toLowerCase();
  return name === "AbortError" || message.includes("aborted") || message.includes("the user aborted");
}

function isTimeoutLike(error) {
  const name = error?.name || "";
  const message = String(error?.message || "").toLowerCase();
  return name === "TimeoutError" || message.includes("timed out");
}

async function readPlayersPerfJson(res) {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (error) {
    const repaired = text
      .replace(/\bNaN\b/g, "null")
      .replace(/-Infinity/g, "null")
      .replace(/\bInfinity\b/g, "null");
    try {
      const parsed = JSON.parse(repaired);
      devLog("warn", "[playersperfs] repaired invalid JSON (NaN/Infinity)", error);
      return parsed;
    } catch (repairError) {
      devLog("error", "[playersperfs] JSON parse failed", {
        status: res.status,
        preview: text.slice(0, 400),
        error,
        repairError,
      });
      throw repairError;
    }
  }
}

function wait(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException("Request aborted", "AbortError"));
      return;
    }

    let timeoutId;
    const onAbort = () => {
      window.clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onAbort);
      reject(signal.reason ?? new DOMException("Request aborted", "AbortError"));
    };

    signal?.addEventListener("abort", onAbort, { once: true });
    timeoutId = window.setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
  });
}

function toNumOrNull(val) {
  if (val === null || val === undefined) return null;
  if (typeof val === "string" && val.trim() === "") return null;
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
}

function safeJsonParse(val) {
  if (val == null) return null;
  if (typeof val === "object") return val; // déjà parsé
  if (typeof val !== "string") return null;
  try {
    return JSON.parse(val);
  } catch {
    return null;
  }
}

function isNationalTeamType(teamType) {
  const normalized = String(teamType ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  return normalized === "national_team" || normalized === "national" || normalized === "selection" || normalized === "national_selection";
}

export  function normalizePlayer(raw = {}, { isFallback = false } = {}) {
  const player = { ...raw };
  player.id = player.id ?? `fallback-${Date.now()}`;
  player.first_name = player.first_name ?? "N/A";
  player.last_name = player.last_name ?? "";
  player.position = player.position ?? "N/A";
  player.trainability_level = safeJsonParse(player.trainability_level) ?? player.trainability_level ?? null;

  player.timeinfo_date = player.timeinfo_date ?? "N/A";

  player.fatigue_index = toNumOrNull(player.fatigue_index);
  player.intensity_index = toNumOrNull(player.intensity_index);
  player.mechanical_load_index = toNumOrNull(player.mechanical_load_index);
  player.mechanical_fatigue = toNumOrNull(player.mechanical_fatigue);

  player.MLI = toNumOrNull(player.MLI);
  player.MLI_prev = toNumOrNull(player.MLI_prev);
  player.RII = toNumOrNull(player.RII);
  player.RII_prev = toNumOrNull(player.RII_prev);

  player.sprint_max = toNumOrNull(player.sprint_max);
  player.sprint_max_prev = toNumOrNull(player.sprint_max_prev);
  player.acceleration_max = toNumOrNull(player.acceleration_max);
  player.acceleration_max_prev = toNumOrNull(player.acceleration_max_prev);
  player.deceleration_max = toNumOrNull(player.deceleration_max);
  player.deceleration_max_prev = toNumOrNull(player.deceleration_max_prev);
  player.global_distance = toNumOrNull(player.global_distance);
  player.global_distance_prev = toNumOrNull(player.global_distance_prev);
  player.COD_number = toNumOrNull(player.COD_number);
  player.COD_number_prev = toNumOrNull(player.COD_number_prev);

  player.time_on_pitch_min = toNumOrNull(player.time_on_pitch_min ?? (
    player.time_on_pitch_s != null ? player.time_on_pitch_s / 60 : null
  ));

  player.time_on_pitch_min_prev = toNumOrNull(player.time_on_pitch_min_prev ?? (
    player.time_on_pitch_s_prev != null ? player.time_on_pitch_s_prev / 60 : null
  ));

  player.hsr_distance_m = toNumOrNull(player.hsr_distance_m);
  player.hsr_distance_m_prev = toNumOrNull(player.hsr_distance_m_prev);

player.spr_distance_m = toNumOrNull(player.spr_distance_m ?? player.SPR ?? null);
player.spr_distance_m_prev = toNumOrNull(player.spr_distance_m_prev ?? null); 
  player.accel_count = toNumOrNull(player.accel_count);
  player.accel_count_prev = toNumOrNull(player.accel_count_prev);

  player.decel_count = toNumOrNull(player.decel_count);
  player.decel_count_prev = toNumOrNull(player.decel_count_prev);

  player.sprint_count = toNumOrNull(player.sprint_count);
  player.sprint_count_prev = toNumOrNull(player.sprint_count_prev);
  player.power_wkg = toNumOrNull(player.power_wkg);
  player.raw_max_power = toNumOrNull(player.raw_max_power);
  player.trainability_index = toNumOrNull(player.trainability_index);
  player.trainability_index_prev = toNumOrNull(player.trainability_index_prev);

  player.at_risk_muscle_group = player.at_risk_muscle_group ?? "None";
  player.at_risk_muscle_side = player.at_risk_muscle_side ?? "N/A";
  player.at_risk_muscle_fatigue = toNumOrNull(player.at_risk_muscle_fatigue);

  player.subjective_fatigue = player.subjective_fatigue ?? "N/A";
  player.subjective_sleep_quality = player.subjective_sleep_quality ?? "N/A";
  player.subjective_stress = player.subjective_stress ?? "N/A";
  player.subjective_mood = player.subjective_mood ?? "N/A";
  player.subjective_pain_list = player.subjective_pain_list ?? "N/A";
  player.subjective_modification_date = player.subjective_modification_date ?? "N/A";

  player.profile_picture = player.profile_picture ?? null;

  // Player status — keep roster visible unless explicitly disabled
  player.player_enabled = !(
    player.player_enabled === false ||
    player.player_enabled === 0 ||
    player.player_enabled === "false" ||
    player.player_enabled === "0"
  );

  player.__isFallback = Boolean(isFallback);

  return player;
}

export function PlayersPerfProvider({
  children,
  token,
  refreshIntervalMs = 12 * 60 * 60 * 1000, // 12h
}) {
  const { season } = useSeason();
  const [state, setState] = useState(() => emptyPerfState({ loading: true }));
  const refetchRef = React.useRef(null);
  const cacheRef = React.useRef(new Map());

  useEffect(() => {
    cacheRef.current.clear();
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    let intervalId;
    const requestSeq = { current: 0 };

    async function fetchAvatars() {
      try {
        const res = await fetchAuth(`/playersperfs/avatars?season=${encodeURIComponent(season)}`, {
          timeoutMs: PLAYERS_PERF_TIMEOUT_MS,
        });
        if (!res.ok) {
          devLog("warn", "[playersperfs] avatars skipped", res.status);
          return;
        }
        const payload = await res.json();
        const avatars = payload?.avatars;
        if (cancelled || !avatars) return;
        setState((prev) => {
          const next = mergeAvatarsIntoState(prev, avatars);
          cacheRef.current.set(season, { ...next, loading: false, error: null });
          return next;
        });
      } catch (error) {
        devLog("warn", "[playersperfs] avatars failed", error);
      }
    }

    async function fetchPlayersPerf() {
      if (!token || parseSeasonStartYear(season) === null) {
        if (!cancelled) {
          setState(emptyPerfState({ loading: false }));
        }
        return;
      }

      const seq = ++requestSeq.current;

      try {
        let data = null;

        for (let attempt = 0; attempt <= PLAYERS_PERF_RETRY_DELAYS_MS.length; attempt += 1) {
          try {
            const res = await fetchAuth(`/playersperfs?season=${encodeURIComponent(season)}`, {
              timeoutMs: PLAYERS_PERF_TIMEOUT_MS,
            });

            if (!res.ok) {
              const text = await res.text().catch(() => "");
              throw new Error(`Failed /playersperfs: ${res.status} ${text}`);
            }

            data = await readPlayersPerfJson(res);
            break;
          } catch (error) {
            if (isAbortLike(error) || cancelled || seq !== requestSeq.current) {
              return;
            }
            if (isTimeoutLike(error) || attempt >= PLAYERS_PERF_RETRY_DELAYS_MS.length) {
              throw error;
            }
            await wait(PLAYERS_PERF_RETRY_DELAYS_MS[attempt]);
          }
        }

        if (cancelled || seq !== requestSeq.current) return;

        const nextState = buildPerfState(data);
        devLog("info", "[playersperfs] loaded", {
          season,
          rawCount: Array.isArray(data?.players) ? data.players.length : 0,
          enabledCount: nextState.players.length,
          allCount: nextState.allPlayers.length,
          clubId: nextState.clubInfo?.id || null,
        });
        cacheRef.current.set(season, nextState);
        if (!cancelled && seq === requestSeq.current) {
          setState(nextState);
        }
        fetchAvatars();
      } catch (e) {
        if (cancelled || seq !== requestSeq.current || isAbortLike(e)) {
          devLog("warn", "[playersperfs] ignored stale/abort error", e);
          return;
        }

        devLog("error", "[playersperfs] load failed", { season, error: e });
        const cached = cacheRef.current.get(season);
        setState((prev) => {
          const keepPlayers = (cached?.allPlayers?.length ? cached : null)
            || (prev.allPlayers?.length ? prev : null);
          if (keepPlayers) {
            return {
              ...keepPlayers,
              loading: false,
              error: e,
              isFallbackData: true,
            };
          }
          return emptyPerfState({ loading: false, error: e, clubInfo: prev.clubInfo || cached?.clubInfo || null });
        });
      }
    }

    refetchRef.current = fetchPlayersPerf;

    if (!token) {
      setState(emptyPerfState({ loading: false }));
      return () => {};
    }

    const cached = cacheRef.current.get(season);
    if (cached) {
      setState({ ...cached, loading: true, error: null });
    } else {
      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));
    }

    fetchPlayersPerf();
    intervalId = setInterval(fetchPlayersPerf, refreshIntervalMs);

    return () => {
      cancelled = true;
      requestSeq.current += 1;
      if (intervalId) clearInterval(intervalId);
    };
  }, [season, token, refreshIntervalMs]);

  const value = useMemo(
    () => ({
      ...state,
      isNationalTeam: Boolean(state.clubInfo?.is_national_team) || isNationalTeamType(state.clubInfo?.team_type),
      getPlayerById: (id) => state.playersById[id] || null,
      refetch: async () => {
        if (refetchRef.current) {
          return await refetchRef.current();
        }
      },
    }),
    [state]
  );

  return (
    <PlayersPerfContext.Provider value={value}>
      {children}
    </PlayersPerfContext.Provider>
  );
}

export function usePlayersPerf() {
  const ctx = useContext(PlayersPerfContext);
  if (!ctx) {
    throw new Error("usePlayersPerf must be used inside <PlayersPerfProvider />");
  }
  return ctx;
}
